import LinkifyIt from 'linkify-it';
import { fromEvent, Observable } from 'rxjs';
import { filter, share, tap } from 'rxjs/operators';
import { isPresentationalPhrasingContent } from '../../helpers/dom/content';
import { getElementContents } from '../../helpers/dom/element';
import { isText } from '../../helpers/dom/node';
import { paintNodesInRange } from '../../helpers/dom/paint';
import { createPointWithEndOfRange } from '../../helpers/dom/point';
import { ignoreDuringCompositions } from '../../helpers/ignoreDuringCompositions';
import { addLinkToRange, nodeHasLink, nodeIsLink } from '../../helpers/link';
import { InteractionAction, InteractionHandler, InteractionTrigger } from '../../interactionEvent';
import { createConnectingService } from '../base/connectingService';
import { NULL_SERVICE, Service } from '../base/service';
import { ElementSelectionService } from '../dom/elementSelectionService';
import { ElementService } from '../dom/elementService';
import { decorateLink } from './htmlLinkService';

export type HtmlAutolinkServiceOptions = {
  onInteraction?: InteractionHandler;
};

export function createHtmlAutolinkService(
  element$: Observable<Element | null>,
  selectionService: ElementSelectionService,
  options: HtmlAutolinkServiceOptions = {},
): Service {
  return createConnectingService(
    element$,
    () => NULL_SERVICE,
    element => new HtmlAutolinkService(element, selectionService, options),
  );
}

interface HtmlAutolinkState {
  textNode: Text;
  url: string;
  startOffset: number;
  endOffset: number;
  cursorOffset: number;
}

export class HtmlAutolinkService extends ElementService<Element> {
  constructor(
    element: Element,
    selectionService: ElementSelectionService,
    options: HtmlAutolinkServiceOptions = {},
  ) {
    super(element, {
      effects$: fromEvent<KeyboardEvent>(element, 'keydown').pipe(
        ignoreDuringCompositions(element),
        // Tab as a traditional autocomplete; Space and Enter as an autocomplete when ending a word or line.
        filter(event => event.key === 'Tab' || event.key === ' ' || event.key === 'Enter'),
        tap(event => {
          if (event.defaultPrevented || !this.canAutolink) {
            return;
          }

          // Don't insert a Tab or change focus, but do enter a space or new line.
          if (event.key === 'Tab') {
            event.preventDefault();
          }

          const didAutolink = this.autolink();

          if (didAutolink && options.onInteraction) {
            options.onInteraction({
              trigger: InteractionTrigger.KEYBOARD_TYPING,
              action: InteractionAction.SET_LINK,
            });
          }
        }),
        share(),
      ),
    });

    this.selectionService = selectionService;
    this.linkify = new LinkifyIt({
      fuzzyLink: true,
      fuzzyEmail: true,
    });
  }

  private readonly selectionService: ElementSelectionService;
  private readonly linkify: LinkifyIt.LinkifyIt;

  private get state(): HtmlAutolinkState | undefined {
    const { selection } = this.selectionService;
    if (!selection || !selection.isCollapsed) {
      return undefined;
    }

    const { focusNode, focusOffset } = selection;
    if (!isText(focusNode)) {
      return undefined;
    }

    const textNode = focusNode;
    const text = textNode.data;
    const textBeforeCursor = text.substring(0, focusOffset);
    const textBeforePunctuation = textBeforeCursor.replace(/[\s.,!?:;"'()[\]…]+$/, '');
    if (!this.linkify.test(textBeforePunctuation)) {
      return undefined;
    }

    const matches = this.linkify.match(textBeforeCursor);
    if (!matches || matches.length === 0) {
      return undefined;
    }

    const match = matches[0];
    if (match.lastIndex !== textBeforePunctuation.length) {
      return undefined;
    }

    return {
      textNode,
      url: match.url,
      startOffset: match.index,
      endOffset: match.lastIndex,
      cursorOffset: focusOffset,
    };
  }

  private get canAutolink(): boolean {
    return !!this.state;
  }

  private autolink(): boolean {
    const { state } = this;
    if (!state) {
      return false;
    }

    const { document, element, selectionService } = this;
    const { textNode, url, startOffset, endOffset, cursorOffset } = state;
    const textAfterUrl = textNode.data.substring(endOffset);
    const cursorOffsetAfterUrl = cursorOffset - endOffset;

    // Add the link.
    const urlRange = document.createRange();
    urlRange.setStart(textNode, startOffset);
    urlRange.setEnd(textNode, endOffset);
    const insideLinkRange = addLinkToRange(element, urlRange, link => decorateLink(link, url));

    // If there text after the link, restore the cursor there.
    const pointAtEndOfLinkContents = createPointWithEndOfRange(insideLinkRange);
    const nextNode = pointAtEndOfLinkContents.findNextNode(element);
    if (textAfterUrl && nextNode && isText(nextNode) && nextNode.data === textAfterUrl) {
      selectionService.moveToOffsetInContainer(nextNode, cursorOffsetAfterUrl);
      return true;
    }

    // Otherwise, use paintNodesInRange to move the cursor after the link, adding an insertion point if needed.
    insideLinkRange.collapse(false);
    const afterLinkRange = paintNodesInRange(element, insideLinkRange, {
      shouldPaintNode: node => nodeHasLink(node, element),
      shouldSplitElement: node => isPresentationalPhrasingContent(node) || nodeIsLink(node),
      paintElement: node => (nodeIsLink(node) ? getElementContents(node) : node),
    });

    // Move to the end of resulting range.
    const afterLinkPoint = createPointWithEndOfRange(afterLinkRange);
    selectionService.moveToPoint(afterLinkPoint);
    return true;
  }
}
